import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { useUser } from '../../context/UserContext';
import { recapturaFicha, recapturaGestion, recapturaPropuesta } from '../../api/apiConfig';
import { BENEFICIOS, RESULTADOS_GESTION, fmtMM, pillClass, unwrap } from './recapturaUtils';

const RecapturaFicha = () => {
  const { rut } = useParams();
  const location = useLocation();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cliente, setCliente] = useState(null);
  const [gestiones, setGestiones] = useState([]);
  const [propuestas, setPropuestas] = useState([]);

  const [resultado, setResultado] = useState(RESULTADOS_GESTION[0]);
  const [comentario, setComentario] = useState('');
  const [proxima, setProxima] = useState('');

  const [commercialComment, setCommercialComment] = useState('');
  const [beneficio, setBeneficio] = useState('');
  const [propuestaTxt, setPropuestaTxt] = useState('');
  const [compromiso, setCompromiso] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = unwrap(
        await recapturaFicha(user, {
          rut: decodeURIComponent(rut || ''),
          cliente_id: location.state?.clienteId,
        }),
      );
      if (!resp.ok) throw new Error(resp.error || 'No encontrado');
      setCliente(resp.cliente);
      setGestiones(resp.gestiones || []);
      setPropuestas(resp.propuestas || []);
      const lastProp = (resp.propuestas || [])[0];
      if (lastProp) {
        setCommercialComment(lastProp.comentario_comercial || '');
        setBeneficio(lastProp.beneficio || '');
        setPropuestaTxt(lastProp.propuesta || '');
        setCompromiso(lastProp.compromiso || '');
      }
    } catch (e) {
      setError(e.message || 'Error al cargar ficha');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && rut) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, rut]);

  const saveGestion = async () => {
    if (!comentario.trim()) {
      window.alert('Ingresa un comentario de gestión.');
      return;
    }
    setSaving(true);
    try {
      const resp = unwrap(
        await recapturaGestion(user, {
          cliente_id: cliente.id,
          resultado,
          comentario: comentario.trim(),
          proxima_accion: proxima.trim() || null,
        }),
      );
      if (!resp.ok) throw new Error(resp.error || 'No se pudo guardar');
      setComentario('');
      setProxima('');
      await load();
    } catch (e) {
      window.alert(e.message || 'Error al guardar seguimiento');
    } finally {
      setSaving(false);
    }
  };

  const saveProp = async (enviar) => {
    setSaving(true);
    try {
      const resp = unwrap(
        await recapturaPropuesta(user, {
          cliente_id: cliente.id,
          enviar,
          comentario_comercial: commercialComment,
          beneficio,
          propuesta: propuestaTxt,
          compromiso,
        }),
      );
      if (!resp.ok) throw new Error(resp.error || 'No se pudo guardar propuesta');
      window.alert(enviar ? 'Propuesta enviada a Gerencia.' : 'Propuesta guardada.');
      await load();
    } catch (e) {
      window.alert(e.message || 'Error en propuesta');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-600">
        <AiOutlineLoading3Quarters className="animate-spin" /> Cargando ficha…
      </div>
    );
  }

  if (error || !cliente) {
    return (
      <div className="p-6">
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm mb-3">{error || 'Sin datos'}</div>
        <Link to="/recaptura/matriz" className="text-sm text-blue-700">Volver a matriz</Link>
      </div>
    );
  }

  const ultimaProp = propuestas[0];

  return (
    <div className="p-6 space-y-4">
      <div className="seccion-datos">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{cliente.cliente || cliente.rut}</h1>
            <p className="text-sm text-gray-500">
              {cliente.rut} · {cliente.categoria} · Ejecutivo: {cliente.ejecutivo || '—'}
            </p>
          </div>
          <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${pillClass(cliente.etapa)}`}>
            {cliente.etapa}
          </span>
        </div>
        <div className="flex gap-2 mt-3">
          <Link to="/recaptura/matriz" className="btn-primary text-sm py-2 px-3" style={{ textDecoration: 'none' }}>
            Volver a matriz
          </Link>
          <Link to="/recaptura/aprobaciones" className="text-sm py-2 px-3 border rounded-lg" style={{ textDecoration: 'none' }}>
            Ir a aprobaciones
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {[
          ['Cesiones Interfactor', cliente.cesiones_if],
          ['Cesiones con bancos', cliente.cesiones_banco],
          ['Otros factoring', cliente.cesiones_otros],
          ['Volumen recuperable est.', cliente.volumen_recuperable],
        ].map(([label, val]) => (
          <div key={label} className="seccion-datos">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-2xl font-bold text-gray-800 mt-1">{fmtMM(val)}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="seccion-datos">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Seguimiento de gestión</h2>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Resultado</label>
          <select className="w-full px-3 py-2 border rounded-lg text-sm mb-3" value={resultado} onChange={(e) => setResultado(e.target.value)}>
            {RESULTADOS_GESTION.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Comentario de gestión</label>
          <textarea
            className="w-full px-3 py-2 border rounded-lg text-sm mb-3"
            rows={5}
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            placeholder="Registrar lo conversado y el siguiente paso"
          />
          <label className="block text-xs font-semibold text-gray-600 mb-1">Próxima acción / fecha</label>
          <input
            className="w-full px-3 py-2 border rounded-lg text-sm"
            value={proxima}
            onChange={(e) => setProxima(e.target.value)}
            placeholder="Ej. Llamar el 10-11-2026"
          />
          <button type="button" className="btn-primary mt-3 text-sm" disabled={saving} onClick={saveGestion}>
            Guardar seguimiento
          </button>
        </div>

        <div className="seccion-datos">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Diagnóstico y oportunidad</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
            {[
              ['Competidor', cliente.competidor],
              ['Motivo validado MGC', cliente.motivo_mgc],
              ['Última operación IF', cliente.ultima_op_if || 'Sin operación'],
            ].map(([k, v]) => (
              <div key={k} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-xs text-gray-500">{k}</div>
                <div className="font-semibold text-sm mt-1 break-words">{v || '—'}</div>
              </div>
            ))}
          </div>

          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
            <h3 className="font-semibold text-gray-800 mb-3">Propuesta de Negocio</h3>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Comentario comercial</label>
            <textarea className="w-full px-3 py-2 border rounded-lg text-sm mb-2" rows={3} value={commercialComment} onChange={(e) => setCommercialComment(e.target.value)} />
            <label className="block text-xs font-semibold text-gray-600 mb-1">Beneficio requerido</label>
            <select className="w-full px-3 py-2 border rounded-lg text-sm mb-2" value={beneficio} onChange={(e) => setBeneficio(e.target.value)}>
              <option value="">Seleccionar beneficio</option>
              {BENEFICIOS.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Propuesta</label>
            <textarea className="w-full px-3 py-2 border rounded-lg text-sm mb-2" rows={3} value={propuestaTxt} onChange={(e) => setPropuestaTxt(e.target.value)} />
            <label className="block text-xs font-semibold text-gray-600 mb-1">Compromiso de negocio</label>
            <textarea className="w-full px-3 py-2 border rounded-lg text-sm mb-2" rows={3} value={compromiso} onChange={(e) => setCompromiso(e.target.value)} />
            <div className="flex flex-wrap gap-2 mt-2">
              <button type="button" className="btn-primary text-sm" disabled={saving} onClick={() => saveProp(false)}>Guardar propuesta</button>
              <button type="button" className="text-sm py-2 px-3 border rounded-lg bg-white" disabled={saving} onClick={() => saveProp(true)}>
                Enviar a Gerencia Comercial / Riesgo
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">La aprobación no equivale a una cesión efectiva (Cursado).</p>
            {ultimaProp && (
              <p className="text-sm mt-3">
                Estado de aprobación:{' '}
                <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${pillClass(ultimaProp.estado)}`}>
                  {ultimaProp.estado}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="seccion-datos">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Historial de gestiones y comentarios</h2>
        <div className="border-l-2 border-slate-200 pl-4 space-y-4">
          {gestiones.length === 0 && <p className="text-sm text-gray-500 italic">Sin gestiones registradas.</p>}
          {gestiones.map((g) => (
            <div key={g.id}>
              <div className="font-semibold text-sm">{g.created_at} · {g.resultado}</div>
              <div className="text-xs text-gray-500">{g.autor}</div>
              <p className="text-sm mt-1">{g.comentario}</p>
              {g.proxima_accion && <p className="text-xs text-gray-600 mt-1">Próxima: {g.proxima_accion}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RecapturaFicha;
